import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { orgTreeApi } from '@/services/org-tree'

type UseGoogleMapsApiKeyOptions = {
  serviceInstanceReference?: string | number | null
  enabled?: boolean
}

export type MapSearchProvider = 'googlemaps' | 'openstreetmap'

export const normalizeMapSearchProvider = (value: unknown): MapSearchProvider => {
  const normalized = String(value ?? '').trim().toLowerCase().replace(/[\s_-]/g, '')
  return normalized === 'google' || normalized === 'googlemaps'
    ? 'googlemaps'
    : 'openstreetmap'
}

export const useGoogleMapsApiKey = ({ serviceInstanceReference, enabled = true }: UseGoogleMapsApiKeyOptions = {}) => {
  const normalizedReference = String(serviceInstanceReference ?? '').trim()

  const serviceInstanceQuery = useQuery({
    queryKey: ['google-maps-service-instance', normalizedReference],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(normalizedReference),
    enabled: enabled && Boolean(normalizedReference),
    staleTime: 60_000,
  })

  const resolvedServiceInstanceReference = useMemo(() => {
    if (serviceInstanceQuery.data?.id != null) {
      return serviceInstanceQuery.data.id
    }

    if (normalizedReference && /^\d+$/.test(normalizedReference)) {
      return Number(normalizedReference)
    }

    return normalizedReference || null
  }, [normalizedReference, serviceInstanceQuery.data?.id])

  const mapsConfigQuery = useQuery({
    queryKey: ['google-maps-client-config', resolvedServiceInstanceReference],
    queryFn: () => orgTreeApi.getServiceInstanceMapsClientConfig(resolvedServiceInstanceReference as string | number),
    enabled: enabled && resolvedServiceInstanceReference != null && !serviceInstanceQuery.isLoading,
    staleTime: 60_000,
  })

  const mapsApiKeyFromCredentials = useMemo(() => {
    const value = mapsConfigQuery.data?.api_key
    if (typeof value !== 'string') {
      return ''
    }

    return value.trim()
  }, [mapsConfigQuery.data?.api_key])

  const mapsApiKey = useMemo(() => {
    if (mapsApiKeyFromCredentials) {
      return mapsApiKeyFromCredentials
    }

    return String(import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '').trim()
  }, [mapsApiKeyFromCredentials])

  const mapProvider = useMemo(() => {
    return normalizeMapSearchProvider(
      mapsConfigQuery.data?.provider
      ?? serviceInstanceQuery.data?.map_search_provider
      ?? 'openstreetmap',
    )
  }, [mapsConfigQuery.data?.provider, serviceInstanceQuery.data?.map_search_provider])

  return {
    mapsApiKey,
    mapProvider,
    isLoading: enabled && (serviceInstanceQuery.isLoading || mapsConfigQuery.isLoading),
    source: mapsApiKeyFromCredentials ? 'credentials' as const : 'env' as const,
    credentialSource: mapsConfigQuery.data?.source ?? null,
  }
}
